/**
 * Fraud Warning Page - Digital On Ramp (Cash-In) Flow
 *
 * Displays important warnings about cryptocurrency transactions being irreversible
 * and common scam scenarios. User must acknowledge before proceeding.
 */

import { t } from "@lingui/core/macro";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useEffect } from "react";
import { FixedBottomBar } from "@/components/layout/FixedBottomBar";
import { RampsButton } from "@/components/ramps/Button";
import { RampsText } from "@/components/ramps/Text";
import { Progress } from "@/components/ui/progress";
import { useContinue } from "@/hooks/useContinue";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";

function FraudWarningPage() {
	const search = Route.useSearch();
	const handleBack = useNavigateBack();
	const handleContinue = useContinue();
	const { trackPageView, trackEvent } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("fraud-warning", { page_name: "fraud-warning" });
	}, [trackPageView]);

	const handleAgreeAndContinue = () => {
		trackEvent("fraud_warning_acknowledged", {
			flow_type: "deposit",
		} as Record<string, unknown>);

		handleContinue(search);
	};

	return (
		<div className="relative flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Header with back button */}
			<div className="flex items-center justify-start p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
					aria-label={t`Back`}
				>
					<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-4 px-4">
				<Progress
					value={75}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-6 pb-40">
				{/* Status Icon with gradient background */}
				<div className="mb-6 flex justify-center">
					<div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-ramps-status">
						<ShieldCheck
							size={32}
							style={{ color: "var(--ramps-text)" }}
							strokeWidth={1}
						/>
					</div>
				</div>

				{/* Title */}
				<RampsText
					variant="title-lg"
					color="primary"
					className="mb-4 text-center"
					style={{ fontWeight: 500 }}
				>
					{t`Fraud prevention`}
				</RampsText>

				{/* Main Warning Text */}
				<RampsText variant="body-sm" color="secondary">
					{t`Cryptocurrency transactions are not reversible and cryptocurrency trading can expose you to scams.`}
				</RampsText>

				{/* Spacing */}
				<div className="mb-6" />

				{/* Scenario Warning */}
				<RampsText variant="body-sm" color="secondary" className="mb-1">
					{t`Please do not proceed if you are unsure of the purpose of your transaction or if:`}
				</RampsText>

				{/* Warning Points */}
				<ul className="list-disc space-y-4 pl-5 [&_li::marker]:text-[10px]">
					<li>
						<RampsText variant="body-sm" color="secondary">
							{t`Someone you met online asked you to make this transaction.`}
						</RampsText>
					</li>
					<li>
						<RampsText variant="body-sm" color="secondary">
							{t`Someone provided a link leading you to this transaction.`}
						</RampsText>
					</li>
					<li>
						<RampsText variant="body-sm" color="secondary">
							{t`You have been told this transaction is to make a purchase, secure employment, or you have been promised an unusually high return.`}
						</RampsText>
					</li>
				</ul>

				{/* Spacing */}
				<div className="mb-6" />

				{/* Disclaimer */}
				<RampsText variant="body-sm" color="secondary" className="mb-8">
					{t`Before deciding to invest or trade cryptocurrencies you should carefully consider your investment objectives, level of experience, and risk appetite.`}
				</RampsText>
			</div>

			{/* Continue Button - Fixed at bottom with gradient fade */}
			<FixedBottomBar className="bg-gradient-to-b from-transparent via-white/50 to-white px-6 pt-8 pb-10">
				<RampsButton
					onClick={handleAgreeAndContinue}
					className="h-12 w-full rounded-lg bg-black font-medium text-white hover:bg-gray-800"
				>
					{t`Agree and continue`}
				</RampsButton>
			</FixedBottomBar>
		</div>
	);
}

export const Route = createFileRoute("/ramps/fraud-warning")({
	component: FraudWarningPage,
});
